'use client';

import { cn } from '@popow/infra/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Dialog, DialogPortal } from '@/components/ui/dialog';

interface CommonModalProps {
  /**
   * 모달 열림/닫힘 상태
   */
  isOpen: boolean;
  /**
   * 모달 닫기 핸들러
   */
  onClose: () => void;
  /**
   * 모달 제목 (선택사항)
   */
  title?: string;
  /**
   * 모달 본문 설명 (선택사항)
   * string: 단일 문단
   * string[]: 여러 문단으로 렌더링
   * React.ReactNode: 커스텀 컴포넌트
   */
  description?: string | string[] | React.ReactNode;
  /**
   * 이미지 또는 커스텀 컨텐츠 (선택사항)
   */
  image?: React.ReactNode;
  /**
   * Primary 액션 버튼 텍스트
   */
  primaryActionText: string;
  /**
   * Primary 액션 핸들러
   */
  onPrimaryAction: () => void;
  /**
   * Secondary 액션 버튼 텍스트 (선택사항)
   */
  secondaryActionText?: string;
  /**
   * Secondary 액션 핸들러 (선택사항)
   */
  onSecondaryAction?: () => void;
  /**
   * Primary 버튼 로딩 상태 (선택사항)
   */
  isPrimaryLoading?: boolean;
  /**
   * 커스텀 className (선택사항)
   */
  className?: string;
  /**
   * Dialog container bottom padding override (optional)
   * Default matches Figma spec (pb-[21px]).
   */
  containerPaddingBottomClassName?: string;
  /**
   * Body padding override (optional)
   * Default: title ? 'py-6' : 'pb-6 pt-0'
   */
  bodyPaddingClassName?: string;
}

/**
 * Description 렌더링 헬퍼 함수
 */
const renderDescription = (description: string | string[] | React.ReactNode) => {
  const descriptionClassName = cn(
    'text-center',
    // Figma: Body 1/Normal/Regular - Mona Sans Regular 16px (137.5% line-height)
    'font-mona-sans text-body-1-normal-regular',
    // Figma: Semantic/Label/Normal
    'text-semantic-label-normal',
  );

  if (typeof description === 'string') {
    return <p className={cn(descriptionClassName, 'whitespace-pre-line')}>{description}</p>;
  }

  if (Array.isArray(description)) {
    return (
      <div className="flex flex-col gap-3">
        {description.map((text, index: number) => (
          <p key={`${text}-${index}`} className={descriptionClassName}>
            {text}
          </p>
        ))}
      </div>
    );
  }

  return description;
};

/**
 * CommonModal - Figma 디자인 시스템 기반 공통 모달 컴포넌트
 *
 * shadcn/ui Dialog를 기반으로 Figma Bottom Sheet 디자인을 적용한 컴포넌트입니다.
 * 하단에서 올라오는 Bottom Sheet 스타일로 표시됩니다.
 *
 * @example
 * ```tsx
 * <CommonModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="확인이 필요합니다"
 *   description="이 작업을 계속 진행하시겠습니까?"
 *   primaryActionText="확인"
 *   onPrimaryAction={handleConfirm}
 *   secondaryActionText="취소"
 *   onSecondaryAction={() => setIsOpen(false)}
 * />
 * ```
 */
export const CommonModal: React.FC<CommonModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  image,
  primaryActionText,
  onPrimaryAction,
  secondaryActionText,
  onSecondaryAction,
  isPrimaryLoading = false,
  className,
  containerPaddingBottomClassName = 'pb-[21px]',
  bodyPaddingClassName,
}) => {
  // Debounced callback - 중복 클릭 방지 (첫 클릭만 실행)
  const debouncedPrimaryAction = useDebouncedCallback(
    () => {
      onPrimaryAction();
    },
    300, // 300ms 동안 중복 클릭 무시
    { leading: true, trailing: false }, // 첫 클릭만 즉시 실행
  );

  const debouncedSecondaryAction = useDebouncedCallback(
    () => {
      if (onSecondaryAction) {
        onSecondaryAction();
      } else {
        onClose();
      }
    },
    300,
    { leading: true, trailing: false },
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPortal>
        {/* Overlay - Figma: Semantic/Material/Dimmer (52% opacity) */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50',
            // Figma: rgba(23,23,25,0.52) → 프로젝트 디자인 시스템
            'bg-material-dimmer-52',
            // 애니메이션
            'data-[state=closed]:animate-out data-[state=open]:animate-in',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />

        {/* Bottom Sheet Container - Figma: Container with 8px padding */}
        <div className="fixed inset-x-0 bottom-0 z-50 flex items-end justify-center p-2">
          <DialogPrimitive.Content
            className={cn(
              // Figma: Semantic/Background/Elevated/Normal (white)
              'bg-bg-elevated',
              // Figma: 28px border radius (전체)
              'rounded-[28px]',
              // 크기 - 반응형
              // 모바일 (< 768px): 풀 스크린
              'w-full',
              // PC (>= 768px): 최대 430px (Figma 디자인 기준)
              'md:max-w-[430px]',
              // 내부 padding은 하위 요소에서 처리
              containerPaddingBottomClassName,
              // Shadow
              'shadow-lg',
              // 애니메이션 - 하단에서 올라옴
              'data-[state=closed]:animate-out data-[state=open]:animate-in',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
              'duration-200',
              className,
            )}
          >
            {/* Header - title이 있을 때만 표시 */}
            {title ? (
              <>
                <div className="flex flex-col items-center justify-end px-7 py-6">
                  <DialogPrimitive.Title
                    className={cn(
                      'w-full text-center',
                      // Figma: Headline 1/Bold - Mona Sans SemiBold 18px (144.5% line-height, -0.004px letter-spacing)
                      '!text-headline-1-bold font-mona-sans tracking-headline-1',
                      // Figma: Semantic/Label/Strong
                      'text-semantic-label-strong',
                    )}
                  >
                    {title}
                  </DialogPrimitive.Title>
                </div>

                {/* Divider - Figma: Semantic/Line/Normal/Normal with 22% opacity */}
                <div className="h-px w-full bg-semantic-line-normal-normal/22" />
              </>
            ) : (
              // 제목이 없을 때도 접근성을 위해 숨겨진 제목 제공
              <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>
            )}

            {/* Body */}
            <div
              className={cn(
                'flex flex-col items-center gap-7 px-5',
                // title이 있으면 py-6, 없으면 pt-6 pb-6 (상단 패딩 줄이기)
                bodyPaddingClassName ?? (title ? 'py-6' : 'pt-0 pb-6'),
              )}
            >
              {/* Image or Custom Content */}
              {image && (
                <div className="flex w-full items-center justify-center overflow-hidden rounded-lg">
                  {image}
                </div>
              )}

              {/* Description */}
              {description && (
                <DialogPrimitive.Description asChild={true}>
                  <div className="w-full px-2">{renderDescription(description)}</div>
                </DialogPrimitive.Description>
              )}

              {/* Actions */}
              <div className="flex w-full flex-col items-center gap-2">
                {/* Primary Button - Figma: ButtonCta/Solid/Alternative/Xlarge */}
                <button
                  onClick={debouncedPrimaryAction}
                  disabled={isPrimaryLoading}
                  className={cn(
                    // Figma: Cool Neutral/800 background
                    'bg-atomic-cool-neutral-800',
                    // 크기 - Figma: 56px height
                    'flex h-14 w-full items-center justify-center gap-2',
                    // Figma: 12px border radius
                    'rounded-xl',
                    'px-5',
                    // Figma: Headline 2/Medium - Mona Sans Medium 17px (141.2% line-height, 0em letter-spacing)
                    'font-mona-sans text-headline-2-medium tracking-headline-2',
                    // Figma: Semantic/Inverse/Label (white)
                    'text-white',
                    // Hover & Active states
                    'transition-colors hover:bg-atomic-cool-neutral-850',
                    'active:scale-[0.98]',
                    // Disabled state
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {isPrimaryLoading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    primaryActionText
                  )}
                </button>

                {/* Secondary Button (Ghost) - Figma: ButtonCta/Ghost/Assistive/Xlarge */}
                {secondaryActionText && (
                  <button
                    onClick={debouncedSecondaryAction}
                    className={cn(
                      'flex h-14 w-full items-center justify-center',
                      'rounded-xl px-5',
                      'transition-colors hover:bg-fill-normal',
                      'active:scale-[0.98]',
                    )}
                  >
                    <div className="flex flex-col items-start justify-center gap-0.5">
                      <span
                        className={cn(
                          // Figma: Headline 2/Medium - Mona Sans Medium 17px (141.2% line-height, 0em letter-spacing)
                          'font-mona-sans text-headline-2-medium tracking-headline-2',
                          // Figma: Semantic/Label/Neutral with 88% opacity
                          'text-label-neutral',
                        )}
                      >
                        {secondaryActionText}
                      </span>
                      {/* Underline - Figma: Semantic/Line/Normal/Strong with 52% opacity */}
                      <div className="h-px w-full bg-semantic-line-normal-strong/52" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
